/**
 * RSC — Signing session detail.
 * Signer status grid · download signed PDF · correction / wet-sign / embed actions.
 *
 * Polling is delegated to `useSigningSession` (hooks/useSigningSession.ts) —
 * capped exponential back-off (5s → 30s) + `/v3/events` webhook-relay
 * listener. On envelope-complete it fires `PipelineService.onEnvelopeCompleted`
 * to kick the paused cart resumption (server-side). On a webhook-relayed
 * correction event it fires `PipelineService.onCorrectionCompleted` so the
 * audit trail dedupes the SignNow mutation.
 *
 * Legacy reference: SPFx `SigningPanel.tsx:450-506` + `ReviewScreen.tsx:518`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft, Download, Edit3, FileSignature, Monitor } from '@retail-ops/panel-shell/fi';
import { Button, Pill, WidgetCard, KVPair } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { formatLocal } from '@/utils/time';
import type { SigningSessionStatusResponse } from '@retail-ops/shared-types';
import { useSigningSession } from './hooks/useSigningSession';
import { useCorrectionCap } from './hooks/useCorrectionCap';
import { CorrectionService } from './services/CorrectionService';
import { CorrectionCapBanner } from './components/CorrectionCapBanner';
import { onCorrectionCompleted, onEnvelopeCompleted } from '@/services/PipelineService';
import './rsc.css';

interface SignerStatus {
  role: string;
  name: string;
  email: string;
  phone?: string;
  venue: 'inperson' | 'remote';
  method: 'email' | 'sms' | 'qr-handoff' | 'embedded' | 'signature-pad' | 'wet-sign';
  deliveryStatus: 'pending' | 'delivered' | 'opened' | 'signed' | 'bounced' | 'declined';
  correctionCount?: number;
}

interface SessionDetailState {
  loading: boolean;
  error: string | null;
  sessionId: string;
  cartId: string;
  groupId?: string;
  status: string;
  createdAt: string;
  signers: ReadonlyArray<SignerStatus>;
  downloadUrl?: string;
}

const TERMINAL_STATUSES: ReadonlySet<string> = new Set([
  'all-signed',
  'completed',
  'complete',
  'declined',
  'expired',
  'cancelled',
  'voided',
  'error',
]);

function isTerminal(status: string): boolean {
  return TERMINAL_STATUSES.has(status);
}

export function SessionDetail(): JSX.Element {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  useAuth(); // ensure auth context is bootstrapped for downstream API calls.
  const abortRef = useRef<AbortController | null>(null);

  const [state, setState] = useState<SessionDetailState>({
    loading: true,
    error: null,
    sessionId: sessionId ?? '',
    cartId: '',
    status: '',
    createdAt: '',
    signers: [],
  });

  // Single-shot hydration — replaced signer list + groupId come from the
  // authoritative session-status response + /v3/sessions once those carry the
  // full payload. For now we seed with fixture data that the hook below
  // refreshes.
  useEffect(() => {
    if (!sessionId) return;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    void (async (): Promise<void> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const resp = await apiClient.signing.getSessionStatus(sessionId, { signal: ctrl.signal });
        if (ctrl.signal.aborted) return;
        setState({
          loading: false,
          error: null,
          sessionId,
          // PILOT 2026-09-04: this block used to merge the REAL resp.Status with
          // INVENTED signers ("Smith, John / john@example.com"), an invented
          // cartId, groupId and createdAt - and it was NOT dev-gated, so a
          // production viewer saw fabricated customer names presented as this
          // session's signers. Same class of defect as the Documents fixture
          // fallback. We now show only what the server actually returned; the
          // signer list stays empty until /v3/sessions carries the real payload,
          // and the UI says so rather than filling the gap with fiction.
          cartId: '',
          groupId: '',
          status: resp.Status ?? 'unknown',
          createdAt: '',
          signers: [],
        });
      } catch (err) {
        if (!ctrl.signal.aborted) {
          setState((prev) => ({
            ...prev,
            loading: false,
            error: err instanceof Error ? err.message : 'Failed to load session.',
          }));
        }
      }
    })();

    return () => ctrl.abort();
  }, [apiClient, sessionId]);

  // Bridges webhook events → PipelineService helpers (idempotent via hook).
  const handleCompleted = useCallback(
    (status: SigningSessionStatusResponse): void => {
      if (!sessionId) return;
      setState((prev) => ({ ...prev, status: status.Status ?? prev.status }));
      void onEnvelopeCompleted(apiClient, {
        sessionId,
        cartId: state.cartId,
        status,
      });
    },
    [apiClient, sessionId, state.cartId],
  );

  const handleCorrected = useCallback(
    (event: { eventId: string; sessionId?: string; payloadJson?: string }): void => {
      if (!sessionId) return;
      let payload: Record<string, unknown> | undefined;
      if (event.payloadJson) {
        try {
          payload = JSON.parse(event.payloadJson) as Record<string, unknown>;
        } catch {
          payload = undefined;
        }
      }
      void onCorrectionCompleted(apiClient, {
        sessionId,
        eventId: event.eventId,
        payload,
      });
    },
    [apiClient, sessionId],
  );

  // Back-off polling + /v3/events webhook-relay listener.
  const sessionPoll = useSigningSession({
    sessionId,
    apiClient,
    onCompleted: handleCompleted,
    onCorrected: handleCorrected,
  });

  // Keep the status pill synced with the latest poll result.
  useEffect(() => {
    if (!sessionPoll.status) return;
    setState((prev) => (prev.status === sessionPoll.status ? prev : { ...prev, status: sessionPoll.status! }));
  }, [sessionPoll.status]);

  // Cap banner — enforced on THIS envelope. Role scope defaults to 'Primary'
  // since SessionDetail is envelope-wide; the per-role scope surfaces again
  // inside CorrectionDrawer.
  const correctionService = useMemo(() => new CorrectionService(apiClient), [apiClient]);
  const correctionCap = useCorrectionCap({
    sessionId,
    role: 'Primary',
    correctionService,
    apiClient,
  });

  const downloadSigned = async (): Promise<void> => {
    if (!state.groupId) return;
    try {
      const resp = await apiClient.signing.downloadSignedPdf(state.groupId);
      const url = (resp as unknown as { SignedPdfUrl?: string; url?: string }).SignedPdfUrl
        ?? (resp as unknown as { url?: string }).url;
      if (url) window.open(url, '_blank', 'noopener');
    } catch (err) {
      setState((prev) => ({
        ...prev,
        error: err instanceof Error ? err.message : 'Download failed.',
      }));
    }
  };

  if (state.loading) {
    return (
      <div className="rsc-detail">
        <p>Loading session {sessionId}…</p>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="rsc-detail">
        <div className="rsc-banner rsc-banner--err" role="alert">
          {state.error}
        </div>
        <Button variant="ghost" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('..')}>
          Back
        </Button>
      </div>
    );
  }

  return (
    <div className="rsc-detail">
      <div className="rsc-detail__crumb">
        <Button variant="ghost" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('..')}>
          Sessions
        </Button>
        <h1>Session {state.sessionId}</h1>
        <Pill variant="info">Cart {state.cartId}</Pill>
        <Pill variant="muted">{state.status}</Pill>
        {isTerminal(state.status) && <Pill variant="ok">Polling stopped</Pill>}
      </div>

      <WidgetCard title="Session" stripe="pipeline">
        <div className="rsc-detail__crumb">
          <KVPair label="Document group" value={state.groupId ?? '—'} />
          <KVPair label="Created" value={formatLocal(state.createdAt)} />
          <KVPair label="Status" value={state.status} />
          {sessionPoll.lastPolledAt && (
            <KVPair label="Last polled" value={formatLocal(sessionPoll.lastPolledAt)} />
          )}
        </div>
      </WidgetCard>

      <CorrectionCapBanner
        count={correctionCap.count}
        cap={correctionCap.cap}
        scopeLabel={`envelope ${state.sessionId}`}
      />

      <WidgetCard title="Signers" stripe="staff">
        {state.signers.length === 0 && !state.loading && (
          <div style={{ padding: 'var(--sp-6)', color: 'var(--ink-500)' }}>
            The signer list is not available from the server for this session yet.
            Nothing is shown here rather than an approximation — check the cart
            itself for who signed.
          </div>
        )}
        <div className="rsc-signer-grid">
          {state.signers.map((s) => (
            <div key={`${s.role}-${s.email}`} className="rsc-signer-row">
              <div className="rsc-signer-row__body">
                <span className="rsc-signer-row__name">
                  {s.name} <span className="rsc-signer-row__sub">· {s.role}</span>
                </span>
                <span className="rsc-signer-row__sub">
                  {s.email}
                  {s.phone ? ` · ${s.phone}` : ''} · {s.venue} · {s.method}
                </span>
              </div>
              <Pill
                variant={
                  s.deliveryStatus === 'signed'
                    ? 'ok'
                    : s.deliveryStatus === 'bounced' || s.deliveryStatus === 'declined'
                    ? 'err'
                    : s.deliveryStatus === 'opened'
                    ? 'warn'
                    : 'muted'
                }
              >
                {s.deliveryStatus}
              </Pill>
              <Pill variant={(s.correctionCount ?? 0) > 0 ? 'warn' : 'muted'}>
                Corrections: {s.correctionCount ?? 0}
              </Pill>
              <Button
                variant="ghost"
                size="sm"
                startIcon={<Edit3 size={12} />}
                onClick={() => {
                  const q = new URLSearchParams({
                    role: s.role,
                    status: state.status,
                  });
                  if (state.groupId) q.set('groupId', state.groupId);
                  navigate(`correct?${q.toString()}`);
                }}
              >
                Correct
              </Button>
            </div>
          ))}
        </div>
      </WidgetCard>

      <WidgetCard title="Actions" stripe="approvals">
        <div className="rsc-detail__crumb">
          <Button
            variant="primary"
            startIcon={<Download size={14} />}
            onClick={() => void downloadSigned()}
            disabled={!state.groupId}
          >
            Download signed package
          </Button>
          <Button
            variant="ghost"
            startIcon={<FileSignature size={14} />}
            onClick={() => navigate('wet')}
          >
            Wet-sign completion
          </Button>
          <Button
            variant="ghost"
            startIcon={<Monitor size={14} />}
            onClick={() => navigate('embed')}
          >
            Open embedded signer
          </Button>
        </div>
      </WidgetCard>
    </div>
  );
}
